import React from "react";
import clsx from "clsx";
import FieldFocusRing from "./FieldFocusRing";
import { OverflowPolicy, describeOverLimit, getOverflowPolicy } from "components/Forms/utils/overflowPolicy";

const TextFormField = ({ fieldData, isCombed, name, value, onChange, readOnly, overflowPolicy }) => {
    const text = String(value || "");
    const policy = getOverflowPolicy(overflowPolicy);

    const inputStyle = (cssCoords) => ({
        position: "absolute",
        top: cssCoords.top,
        left: cssCoords.left,
        width: cssCoords.width,
        height: cssCoords.height,
    });

    if (isCombed) {
        // A value longer than the boxes (e.g. prefilled) cannot be shown: the cells say so
        const overLimit = describeOverLimit({ policy, maxLength: fieldData.length, length: text.length, isCombed: true });

        const getValue = (cellIndex) => {
            return String(value).charAt(cellIndex) || "";
        };

        const setValue = (cellIndex, newValue) => {
            onChange(name, String(value).slice(0, cellIndex) + newValue + String(value).slice(cellIndex + 1));
        };

        const onCellValueChange = (cellIndex, newValue) => {
            setValue(cellIndex, newValue);
        };

        const onKeyDown = (e, cellIndex) => {
            if (e.key === "Backspace") {
                if (cellIndex > 0) {
                    const prevElement = document.getElementById(`${name}-${cellIndex - 1}`);
                    if (prevElement) {
                        prevElement.focus();
                    }
                }
            } else if (e.key.length === 1) {
                if (cellIndex < fieldData.length - 1) {
                    const nextElement = document.getElementById(`${name}-${cellIndex + 1}`);
                    if (nextElement) {
                        nextElement.focus();
                    }
                }
            } else if (e.key === "Enter") {
                e.preventDefault();
            } else if (e.key === "Tab") {
                if (cellIndex < fieldData.length - 1) {
                    e.preventDefault();
                }
            }
        };

        return (
            <>
                {fieldData.map((field) => {
                    const { cellIndex, cssCoords } = field;
                    return (
                        <FieldFocusRing key={name + cellIndex} cssCoords={cssCoords}>
                            <input
                                id={`${name}-${cellIndex}`}
                                type="text"
                                style={{ ...inputStyle(cssCoords), textAlign: "center" }}
                                name={name + cellIndex}
                                maxLength={1}
                                value={getValue(cellIndex)}
                                onChange={(e) => onCellValueChange(cellIndex, e.target.value)}
                                autoComplete="cc-csc"
                                readOnly={readOnly}
                                disabled={readOnly}
                                onKeyDown={(e) => onKeyDown(e, cellIndex)}
                                data-field-name={name}
                                className={clsx({ "is-over-limit": overLimit })}
                                title={overLimit ?? undefined}
                            />
                        </FieldFocusRing>
                    );
                })}
            </>
        );
    }

    const { cssCoords, maxLength, isMultiline, textStyle } = fieldData;
    // The whole value is shown, also past the limit, so a prefilled value is never silently cut.
    // `maxLength` only stops typing past it, unless this field ignores its limit.
    const inputMaxLength = policy === OverflowPolicy.IGNORE_MAX_LENGTH ? undefined : maxLength;
    const overLimit = describeOverLimit({ policy, maxLength, length: text.length });
    const overLimitProps = { className: clsx({ "is-over-limit": overLimit }), title: overLimit ?? undefined };

    if (isMultiline) {
        return (
            <FieldFocusRing cssCoords={cssCoords}>
                <textarea
                    // Un campo con renglones trae su propio padding (ver getRuledTextareaStyle)
                    style={{ ...inputStyle(cssCoords), resize: "none", ...(textStyle ?? { padding: 5 }) }}
                    name={name}
                    maxLength={inputMaxLength}
                    value={text}
                    onChange={(e) => onChange(name, e.target.value)}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-field-name={name}
                    {...overLimitProps}
                />
            </FieldFocusRing>
        );
    }

    return (
        <FieldFocusRing cssCoords={cssCoords}>
            <input
                type="text"
                style={{ ...inputStyle(cssCoords), paddingLeft: 5 }}
                name={name}
                maxLength={inputMaxLength}
                value={text}
                onChange={(e) => onChange(name, e.target.value)}
                autoComplete="off"
                readOnly={readOnly}
                disabled={readOnly}
                data-field-name={name}
                {...overLimitProps}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                }}
            />
        </FieldFocusRing>
    );
};

export default TextFormField;
